/**
 * Máquina de expressão do domo — orientada a evento, declarativa.
 *
 * ## Por que existe
 *
 * Antes disto a cara do Will era decidida em dois lugares que não se falavam:
 * `dome-map.ts` no servidor (com trava de atividade) e `DOME_BASE` no cliente
 * (sem trava). Os dois mapas já tinham divergido, e a emoção escolhida pelo
 * cérebro viajava no `u1.state.changed` para ser descartada na chegada — o
 * cliente lia só o campo `to`. Resultado observado: das catorze expressões,
 * três eram alcançáveis e duas visíveis.
 *
 * Aqui a expressão deixa de ser função do estado e passa a ser uma **pilha de
 * um lugar só**, com regras que declaram, cada uma:
 *
 * - **gatilho** — o evento ou a leitura que a instala;
 * - **prioridade** — quem corta quem;
 * - **duração** — por quanto tempo se sustenta;
 * - **interrompível** — se aceita ser cortada por igual prioridade;
 * - **fallback** — para onde desce quando acaba, em vez de cortar seco.
 *
 * ## O chão e a sobreposição
 *
 * O estado conversacional dá o **chão** (`DOME_BASE`): sempre existe, nunca
 * expira, não é armazenado. Uma regra instala uma **sobreposição** por cima
 * dele. Quando a sobreposição expira, o rosto volta ao chão sozinho — não é
 * preciso ninguém lembrar de limpá-la.
 *
 * ## A trava de atividade
 *
 * Em dez estados a atividade *é* a fisionomia, e sobreposição abaixo de
 * `CRITICO` não aparece. É a coreografia do app legado: durante a fala o rosto
 * é de quem fala, e a emoção do turno assenta depois, quando volta a
 * `LISTENING`. Sem isto o cérebro devolvia `listening` junto com a resposta e o
 * Will falava de boca fechada com cara de quem escuta — defeito que já chegou a
 * produção uma vez.
 *
 * Tudo aqui é função pura de (estado, sobreposição, agora). Sem React, sem
 * temporizador, sem relógio próprio: quem chama informa `agora`. É o que
 * permite testar catorze expressões e as travas sem montar um componente.
 */

import type { ConversationState, U1EventType } from './conversation.js';
import { isDomeState, type DomeState } from './dome.js';

/* ── Prioridades ─────────────────────────────────────────────────────────── */

/**
 * Camadas de prioridade. Maior corta menor; igual só corta se a atual se
 * declarar interrompível.
 *
 * Os números são espaçados de propósito: uma camada nova entra entre duas
 * existentes sem renumerar as regras já escritas.
 */
export const PRIORIDADE = {
  /** o chão derivado do estado conversacional — não é sobreposição */
  BASE: 0,
  /** deriva de inatividade: cochilar entre turnos */
  OCIOSO: 5,
  /** a emoção que o cérebro escolheu para este turno */
  AFETO: 10,
  /** reação a um acontecimento: ferramenta concluiu, modo de voz mudou */
  REACAO: 20,
  /** falha — atravessa inclusive a trava de atividade */
  CRITICO: 30,
} as const;

/* ── Regras ──────────────────────────────────────────────────────────────── */

export interface RegraDeExpressao {
  readonly dome: DomeState;
  readonly prioridade: number;
  /** por quanto tempo se sustenta. `0` = até algo com direito a substituí-la */
  readonly duracaoMs: number;
  /** aceita ser cortada por uma regra de prioridade igual */
  readonly interrompivel: boolean;
  /**
   * Para onde a face desce quando esta expressão acaba.
   *
   * `null` devolve ao chão do estado. Uma regra encadeada suaviza a saída: uma
   * falha que volta direto para `listening` corta seco; passando por `worried`
   * por um segundo, ela *termina* em vez de sumir.
   */
  readonly fallback: RegraDeExpressao | null;
}

/** Uma regra instalada, com o instante em que perde a vez. */
export interface ExpressaoAtiva extends RegraDeExpressao {
  /** o que a instalou — vai para o painel de depuração */
  readonly origem: string;
  /** `null` quando `duracaoMs` é 0: só sai quando alguém a substituir */
  readonly expiraEm: number | null;
}

/* ── O chão ──────────────────────────────────────────────────────────────── */

/**
 * Expressão de base de cada estado conversacional.
 *
 * Deliberadamente incompleta em relação às catorze: `opportunity`, `completed`,
 * `unclear`, `sad` e `worried` não correspondem a fase nenhuma do runtime — são
 * reações a conteúdo, e chegam por sobreposição.
 */
export const DOME_BASE: Record<ConversationState, DomeState> = {
  IDLE: 'resting',
  CONNECTING: 'waking',
  LISTENING: 'listening',
  USER_SPEAKING: 'listening',
  THINKING: 'thinking',
  PREAMBLE: 'speaking',
  TOOL_RUNNING: 'busy',
  WAITING_APPROVAL: 'awaiting',
  ASSISTANT_SPEAKING: 'speaking',
  INTERRUPTED: 'listening',
  // Modo privado não é descanso conceitualmente, mas visualmente o domo deve
  // parecer inativo — é essa a mensagem para quem está na sala.
  PRIVATE: 'resting',
  RECONNECTING: 'waking',
  ERROR: 'failed',
};

/**
 * Estados em que a atividade **é** a fisionomia.
 *
 * `LISTENING`, `THINKING` e `IDLE` ficam de fora: são justamente as pausas em
 * que a emoção do turno tem lugar para aparecer.
 */
export const ATIVIDADE_TRANCA: ReadonlySet<ConversationState> = new Set<ConversationState>([
  'CONNECTING',
  'USER_SPEAKING',
  'PREAMBLE',
  'TOOL_RUNNING',
  'WAITING_APPROVAL',
  'ASSISTANT_SPEAKING',
  'INTERRUPTED',
  'RECONNECTING',
  'PRIVATE',
  'ERROR',
]);

/* ── Gatilhos ────────────────────────────────────────────────────────────── */

const reacao = (
  dome: DomeState,
  duracaoMs: number,
  fallback: RegraDeExpressao | null = null,
): RegraDeExpressao => ({
  dome,
  prioridade: PRIORIDADE.REACAO,
  duracaoMs,
  interrompivel: true,
  fallback,
});

/** Descida curta e neutra: usada como fallback de reações que não devem cortar seco. */
const assentar = (dome: DomeState, duracaoMs: number): RegraDeExpressao => ({
  dome,
  prioridade: PRIORIDADE.OCIOSO,
  duracaoMs,
  interrompivel: true,
  fallback: null,
});

/**
 * Que expressão cada evento instala.
 *
 * Eventos ausentes desta tabela não mexem na cara — o que não é omissão: a
 * maioria dos eventos muda o *estado*, e o estado já move o chão. Sobreposição
 * é para o que o estado não conta.
 */
export const GATILHOS: Partial<Record<U1EventType, RegraDeExpressao>> = {
  // Reconectar limpa o rosto: `duracaoMs` curto e prioridade de reação para
  // atravessar qualquer coisa que a queda tenha deixado instalada.
  'u1.session.ready': reacao('waking', 1_200),
  'u1.session.recovered': reacao('waking', 1_500),

  // Fim de sessão não expira sozinho — nada mais vai acontecer nesta tela até
  // alguém reconectar. Mas é INTERROMPÍVEL, e a diferença não é estética.
  //
  // Com `interrompivel: false` esta regra virava uma cara que nada removia:
  // não expirava, não cedia a fronteira de turno, não cedia à emoção do
  // cérebro, e nem à sessão recuperada — que tem a mesma prioridade e por isso
  // precisava justamente da permissão de interromper. O Will entrava em
  // repouso e não voltava mais, nem com sessão nova. Aconteceu em produção.
  'u1.session.ended': {
    dome: 'resting',
    prioridade: PRIORIDADE.REACAO,
    duracaoMs: 0,
    interrompivel: true,
    fallback: null,
  },

  // Trabalho profundo é `focus` e não `busy`: roxo para análise longa, laranja
  // para execução curta. A diferença é visível e intencional.
  'u1.deep_work.started': {
    dome: 'focus',
    prioridade: PRIORIDADE.REACAO,
    duracaoMs: 0,
    interrompivel: true,
    fallback: null,
  },
  'u1.deep_work.progress': {
    dome: 'focus',
    prioridade: PRIORIDADE.REACAO,
    duracaoMs: 0,
    interrompivel: true,
    fallback: null,
  },

  'u1.tool.completed': reacao('completed', 2_500, assentar('listening', 600)),
  'u1.deep_work.completed': reacao('completed', 2_800, assentar('listening', 600)),
  'u1.deep_work.cancelled': reacao('resting', 2_000),
  // Antes `u1.mission.updated` — ver a nota do renome em `conversation.ts`.
  // A regra em si não muda: trocar de modo de voz é um acontecimento que vale
  // uma reação curta, e a face assenta em `listening` depois.
  'u1.session.voice_mode_changed': reacao('opportunity', 3_000, assentar('listening', 500)),
  'u1.approval.expired': reacao('worried', 3_000),

  // Falha atravessa a trava de atividade: se o Will falhou no meio da fala, a
  // cara tem que dizer isso — é a única classe de evento que não espera a pausa.
  'u1.tool.failed': {
    dome: 'failed',
    prioridade: PRIORIDADE.CRITICO,
    duracaoMs: 3_000,
    interrompivel: false,
    fallback: assentar('worried', 1_200),
  },
  'u1.error': {
    dome: 'failed',
    prioridade: PRIORIDADE.CRITICO,
    duracaoMs: 5_000,
    interrompivel: false,
    fallback: assentar('worried', 1_500),
  },
};

/* ── Regras que não vêm de evento ────────────────────────────────────────── */

/**
 * A emoção que o cérebro escolheu para o turno.
 *
 * Chega no `u1.state.changed` e fica sem prazo: vale até o próximo turno
 * escolher outra. A trava de atividade cuida de ela só aparecer na pausa.
 */
export function regraDeAfeto(dome: DomeState): RegraDeExpressao {
  return {
    dome,
    prioridade: PRIORIDADE.AFETO,
    duracaoMs: 0,
    interrompivel: true,
    fallback: null,
  };
}

/**
 * Sonolência por inatividade.
 *
 * Prioridade baixa e interrompível de propósito: qualquer coisa que aconteça
 * tem direito de acordar o Will.
 */
export const REGRA_OCIOSO: RegraDeExpressao = {
  dome: 'resting',
  prioridade: PRIORIDADE.OCIOSO,
  duracaoMs: 0,
  interrompivel: true,
  fallback: null,
};

/** Silêncio até o Will cochilar. Longo o bastante para não cochilar numa pausa de conversa. */
export const OCIOSO_APOS_MS = 90_000;

/* ── A máquina ───────────────────────────────────────────────────────────── */

/**
 * Uma regra que não expira e não se deixa interromper é uma cara para sempre.
 *
 * Este é o formato exato de um defeito que chegou a produção: `u1.session.ended`
 * nascia assim e o Will entrava em repouso sem volta. Só `CRITICO` tem licença
 * para isso, porque falha é o único estado que deve resistir a tudo — e mesmo
 * ele tem prazo. A verificação roda na carga do módulo: uma regra assim é erro
 * de programação, e erro de programação deve impedir o boot, não virar aviso.
 */
function conferirGatilhos(): void {
  for (const [tipo, regra] of Object.entries(GATILHOS)) {
    if (regra !== undefined && regra.duracaoMs === 0 && !regra.interrompivel) {
      throw new Error(
        `[expression] ${tipo} declara duração 0 e não-interrompível: ` +
          'nenhum caminho removeria essa expressão. Dê prazo ou deixe interromper.',
      );
    }
  }
}
conferirGatilhos();

export function expirou(atual: ExpressaoAtiva, agora: number): boolean {
  return atual.expiraEm !== null && agora >= atual.expiraEm;
}

function instalar(regra: RegraDeExpressao, agora: number, origem: string): ExpressaoAtiva {
  return {
    ...regra,
    origem,
    expiraEm: regra.duracaoMs > 0 ? agora + regra.duracaoMs : null,
  };
}

/**
 * Tenta instalar uma regra sobre a expressão corrente.
 *
 * Devolve a expressão que passa a valer — a nova, ou a mesma de antes quando a
 * corrente tem o direito de segurar o lugar. Nunca lança: expressão é
 * apresentação, e uma regra recusada não é erro, é a máquina funcionando.
 */
export function aplicarRegra(
  atual: ExpressaoAtiva | null,
  regra: RegraDeExpressao,
  agora: number,
  origem: string,
): ExpressaoAtiva {
  if (atual === null || expirou(atual, agora)) return instalar(regra, agora, origem);
  if (regra.prioridade > atual.prioridade) return instalar(regra, agora, origem);
  if (regra.prioridade === atual.prioridade && atual.interrompivel) {
    return instalar(regra, agora, origem);
  }
  return atual;
}

/**
 * Instala o que um evento pede, se ele pedir alguma coisa.
 *
 * Devolve `atual` sem tocar quando o evento não tem gatilho — o chamador pode
 * comparar por identidade para saber se precisa redesenhar.
 */
export function aplicarEvento(
  atual: ExpressaoAtiva | null,
  tipo: U1EventType,
  agora: number,
): ExpressaoAtiva | null {
  const regra = GATILHOS[tipo];
  if (regra === undefined) return atual;
  return aplicarRegra(atual, regra, agora, tipo);
}

/**
 * Faz o tempo passar: expira o que venceu e desce pelos fallbacks.
 *
 * O laço percorre a cadeia porque um fallback também tem prazo, e um quadro que
 * chegue atrasado (aba em segundo plano, GC longo) pode vencer os dois de uma
 * vez. Sem o laço, a face ficaria presa num fallback já vencido até o próximo
 * evento — que é justamente quando não há evento nenhum.
 */
export function avancar(atual: ExpressaoAtiva | null, agora: number): ExpressaoAtiva | null {
  let corrente = atual;
  while (corrente !== null && expirou(corrente, agora)) {
    const proxima = corrente.fallback;
    if (proxima === null) return null;
    // O fallback herda o instante de expiração da regra que venceu, e não
    // `agora`: se o quadro chegou 2 s atrasado, o fallback já consumiu 2 s dos
    // seus. Contar a partir de `agora` esticaria a cadeia toda a cada engasgo.
    const base = corrente.expiraEm ?? agora;
    corrente = {
      ...proxima,
      origem: `${corrente.origem}→fallback`,
      expiraEm: proxima.duracaoMs > 0 ? base + proxima.duracaoMs : null,
    };
  }
  return corrente;
}

export interface FaceResolvida {
  readonly dome: DomeState;
  /** de onde veio o que está na tela — `'estado'` quando é o chão */
  readonly origem: string;
  /** a sobreposição existe mas está calada pela trava de atividade */
  readonly travada: boolean;
}

/**
 * Que rosto mostrar agora.
 *
 * Não muta nada e não olha o relógio: `agora` vem de quem chama, e `atual` já
 * deve ter passado por `avancar`.
 */
export function resolver(
  estado: ConversationState,
  atual: ExpressaoAtiva | null,
  agora: number,
): FaceResolvida {
  const chao = DOME_BASE[estado];
  if (atual === null || expirou(atual, agora)) {
    return { dome: chao, origem: 'estado', travada: false };
  }
  if (atual.prioridade < PRIORIDADE.CRITICO && ATIVIDADE_TRANCA.has(estado)) {
    return { dome: chao, origem: 'estado', travada: true };
  }
  return { dome: atual.dome, origem: atual.origem, travada: false };
}

/**
 * Lê a sugestão de rosto que o servidor mandou no `u1.state.changed`.
 *
 * Devolve `null` quando a sugestão é apenas o eco do chão daquele estado. O
 * servidor manda `dome` em **toda** transição, e a maioria não é escolha
 * nenhuma — é `DOME_BASE[to]` de volta. Instalar isso como sobreposição criaria
 * uma camada que só copia o chão, e aí a primeira emoção de verdade seria
 * apagada pela transição seguinte. Sobreposição é para o que o estado não conta.
 *
 * Tolerante de propósito: valor fora do catálogo é motivo para ignorar a
 * sugestão, não para derrubar o turno. O chão continua correto sem ela.
 */
export function afetoDeStateChanged(payload: unknown): DomeState | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const registro = payload as Record<string, unknown>;
  const bruto = registro['dome'];
  if (!isDomeState(bruto)) return null;
  const destino = registro['to'];
  if (typeof destino === 'string' && destino in DOME_BASE) {
    const chao = DOME_BASE[destino as ConversationState];
    if (bruto === chao) return null;
  }
  return bruto;
}

/**
 * Estados cuja chegada significa "começou um turno novo".
 *
 * Importa porque a emoção de um turno não tem direito de sobreviver ao
 * seguinte. Sem isto, uma sugestão sem prazo instalada no fim de um turno
 * mascararia o `thinking` do próximo — o Will pensaria com a cara da resposta
 * anterior.
 */
export const RECOMECA_TURNO: ReadonlySet<ConversationState> = new Set<ConversationState>([
  'USER_SPEAKING',
  'THINKING',
]);

/**
 * Limpa o que não sobrevive à entrada num estado.
 *
 * Só derruba sobreposições até `AFETO`: reação e falha atravessam o turno de
 * propósito — uma ferramenta que falhou continua no rosto enquanto o Will
 * explica o que houve.
 */
export function aoEntrarEstado(
  atual: ExpressaoAtiva | null,
  estado: ConversationState,
): ExpressaoAtiva | null {
  if (atual === null) return null;
  if (!RECOMECA_TURNO.has(estado)) return atual;
  return atual.prioridade <= PRIORIDADE.AFETO ? null : atual;
}
